// [DOMAIN ENUMS]: Single source of truth for string-valued domain concepts.
// [WHY]: Producer/consumer mismatches on raw strings have been the #1 recurring
//        bug source throughout this project (ARC-014 cleanup Jan 2026;
//        40+ regression sites found Apr 2026). Enums make the mismatch compile-
//        (or at least lint-) time visible.
// [USE]: Import these constants everywhere instead of string literals for
//        damage types, equipment slots, classes, races, rarities, etc.

export const DAMAGE_TYPES = Object.freeze({
  SLASHING: 'slashing',
  PIERCING: 'piercing',
  BLUDGEONING: 'bludgeoning',
  FIRE: 'fire',
  COLD: 'cold',
  LIGHTNING: 'lightning',
  THUNDER: 'thunder',
  ACID: 'acid',
  POISON: 'poison',
  NECROTIC: 'necrotic',
  RADIANT: 'radiant',
  PSYCHIC: 'psychic',
  FORCE: 'force',
});

// [A.5/A.6 APR 2026]: Values match the lowercase no-underscore convention
// already in use across the runtime (gameData.js, EquipmentPanel slots,
// InventoryItem equipSlot). Do NOT rename values without migrating every
// site that reads/writes them (incl. saved character data). That's a
// sub-project G responsibility.
export const EQUIPMENT_SLOTS = Object.freeze({
  MAIN_HAND: 'mainhand',
  OFF_HAND: 'offhand',
  ARMOR: 'armor',
  SHIELD: 'shield',
  RANGED: 'ranged',
  HEAD: 'head',
  NECK: 'neck',
  CLOAK: 'cloak',
  BELT: 'belt',
  GLOVES: 'gloves',
  FEET: 'feet',
  BOOTS: 'boots',
  RING_1: 'ring1',
  RING_2: 'ring2',
  AMULET: 'amulet',
  AMMO: 'ammo',
  // [PHASE B / #008]: Added to cover values present in BASE_ITEMS rows
  //   (gameData.js: arms, earring, hands, ring, wrist) and source data
  //   (sources/tashas.js, xanathars.js, fizbans.js: back, trinket,
  //   waist). Existing keys above are NOT renamed — that's tracked as
  //   a sub-project G data-migration concern. The new entries make
  //   the EQUIPMENT_SLOT_LABELS map below complete (every slot value
  //   actually present in the data has a UI label).
  ARMS: 'arms',
  EARRING: 'earring',
  // [SUB-PLAN 1R-RENDERGAP STONE 3a / 2026-05-27]: numbered earring slots
  EARRING_1: 'earring1',
  EARRING_2: 'earring2',
  // [SUB-PLAN 1R-eyes-data / 2026-05-27]: dedicated slot for lens/goggle items
  //   (goggles_of_night, eyes_of_the_eagle, eyes_of_minute_seeing). Previously
  //   these used 'head', which rendered them in the Head UI cell. The Eyes gear-grid
  //   cell already existed in EquipmentPanel (Stone 2 BREADCRUMB); this enum entry
  //   wires the data side so items actually land in the correct slot.
  EYES: 'eyes',
  HANDS: 'hands',
  RING: 'ring',
  WRIST: 'wrist',
  BACK: 'back',
  TRINKET: 'trinket',
  WAIST: 'waist',
  // [SUB-PLAN 1R / 2026-05-29]: 'ioun' for Ioun Stones (orbit the head per DMG p.176);
  //   'misc' is the generic no-op indicator the InventoryContext dispatch falls
  //   back to for un-classified magical items that have no equipSlot AND no weapon
  //   properties. The 20 Ioun Stones in BASE_ITEMS now declare equipSlot: 'ioun'.
  IOUN: 'ioun',
  // [SUB-PLAN 1R-followup / 2026-05-28]: Numbered ioun orbit slots per DMG p.176.
  //   BASE_ITEMS rows keep bare 'ioun'; equipSlotMigration.js PAIRED_SLOT_MAP expands
  //   ioun → [ioun1, ioun2, ioun3] at equip-time and on-load migration.
  //   Naming: matches ring1/ring2, earring1/earring2 (no underscore separator).
  IOUN_1: 'ioun1',
  IOUN_2: 'ioun2',
  IOUN_3: 'ioun3',
  MISC: 'misc',
});

// [PHASE B / #008]: Display labels for EQUIPMENT_SLOTS. Single source
//   of truth used by ItemDialog + EditItemDialog — pre-fix the two
//   dropdowns had divergent vocabularies that did not match BASE_ITEMS
//   values, so picking a Cloak from the SearchableItemSelector left
//   the Equipment Slot dropdown showing nothing selected (because
//   neither dialog's hand-rolled options included 'cloak'). The
//   ordering below mirrors a head-to-toe + weapons + special tail.
//   Keys are slot VALUES (not the EQUIPMENT_SLOTS uppercase keys) so
//   `Object.entries` iteration directly produces <option value=> pairs.
export const EQUIPMENT_SLOT_LABELS = Object.freeze({
  // Head / face
  head: 'Head',
  earring: 'Earring',
  // [SUB-PLAN 1R-RENDERGAP STONE 3a / 2026-05-27]: earring1/earring2 labels for
  //   EquipmentPanel gear grid (which already renders these slots). Without these
  //   labels, the ContextMenu slot-selection has no display text for 'earring1'/'earring2'.
  earring1: 'Earring (L)',
  earring2: 'Earring (R)',
  // [SUB-PLAN 1R-eyes-data / 2026-05-27]: Eyes slot — lens/goggle items
  //   (goggles_of_night, eyes_of_the_eagle, eyes_of_minute_seeing) moved from
  //   'head' to 'eyes'. ReferencePicker kind="equipSlot" auto-includes this
  //   entry — ItemDialog + EditItemDialog need no code change.
  eyes: 'Eyes',
  // Neck
  neck: 'Neck (Amulet)',
  amulet: 'Amulet',
  // Torso / back
  cloak: 'Cloak',
  back: 'Back',
  armor: 'Armor / Body',
  // Waist
  belt: 'Belt',
  waist: 'Waist',
  // Arms / hands
  arms: 'Arms (Bracers)',
  wrist: 'Wrist (Bracelet)',
  hands: 'Hands',
  gloves: 'Gloves',
  // Lower body
  boots: 'Boots',
  feet: 'Feet',
  // Fingers
  ring: 'Ring',
  ring1: 'Ring 1',
  ring2: 'Ring 2',
  // Weapons
  mainhand: 'Main Hand',
  offhand: 'Off Hand',
  shield: 'Shield',
  ranged: 'Ranged Weapon',
  ammo: 'Ammunition',
  // Misc / catch-all
  trinket: 'Trinket',
  // [SUB-PLAN 1R / 2026-05-29]: Ioun Stones orbit the head per DMG p.176; they
  //   are never wielded. Floating-around-head is a unique slot — not 'head'
  //   (helms/circlets), not 'mainhand'. Label explicitly flags the floating
  //   nature so the UI message is unambiguous. The dispatch route in
  //   InventoryContext.getEquipSlots reads equipSlot: 'ioun' from BASE_ITEMS.
  ioun: 'Ioun Stone (orbits head)',
  // [SUB-PLAN 1R-followup / 2026-05-28]: Numbered orbit slots. Labels render in
  //   ContextMenuComponent "Equip to {label}" so each slot is unambiguous to the player.
  //   "Orbit 1/2/3" mirrors the numbered ring/earring convention (Ring 1, Ring 2, etc.).
  ioun1: 'Ioun Stone Orbit 1',
  ioun2: 'Ioun Stone Orbit 2',
  ioun3: 'Ioun Stone Orbit 3',
  // [SUB-PLAN 1R / 2026-05-29]: Generic catch-all when a magical item has
  //   neither an equipSlot nor weapon-like properties. The ContextMenuComponent
  //   dispatches on slot to render an appropriate label; 'misc' falls through
  //   without offering an Equip action.
  misc: 'Inventory (no equip)',
});

export const ARMOR_WEIGHTS = Object.freeze({
  LIGHT: 'light',
  MEDIUM: 'medium',
  HEAVY: 'heavy',
  SHIELD: 'shield',
});

export const RARITIES = Object.freeze({
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  VERY_RARE: 'very_rare',
  LEGENDARY: 'legendary',
  ARTIFACT: 'artifact',
});

export const CLASS_IDS = Object.freeze({
  BARBARIAN: 'barbarian',
  BARD: 'bard',
  CLERIC: 'cleric',
  DRUID: 'druid',
  FIGHTER: 'fighter',
  MONK: 'monk',
  PALADIN: 'paladin',
  RANGER: 'ranger',
  ROGUE: 'rogue',
  SORCERER: 'sorcerer',
  WARLOCK: 'warlock',
  WIZARD: 'wizard',
  ARTIFICER: 'artificer',
});

export const RACE_IDS = Object.freeze({
  HUMAN: 'human',
  ELF: 'elf',
  DWARF: 'dwarf',
  HALFLING: 'halfling',
  DRAGONBORN: 'dragonborn',
  GNOME: 'gnome',
  HALF_ELF: 'half-elf',
  HALF_ORC: 'half-orc',
  TIEFLING: 'tiefling',
  AASIMAR: 'aasimar',
});

export const SOURCE_IDS = Object.freeze({
  SRD: 'srd',
  PHB: 'phb',
  TASHAS: 'tashas',
  XANATHARS: 'xanathars',
  FIZBANS: 'fizbans',
  VOLOS: 'volos',
});

export const ITEM_CATEGORIES = Object.freeze({
  WEAPON: 'weapon',
  ARMOR: 'armor',
  CONSUMABLE: 'consumable',
  MAGIC: 'magic',
  WONDROUS: 'wondrous_items',
  TOOL: 'tool',
  GEM: 'gem',
  ART_OBJECT: 'art_object',
  TRADE_GOOD: 'trade_good',
  CONTAINER: 'container',
  AMMUNITION: 'ammunition',
});

export const WEAPON_APPLICABILITY = Object.freeze({
  ALL: 'all',
  MELEE: 'melee',
  RANGED: 'ranged',
  HEAVY_MELEE: 'heavy_melee',
  FINESSE: 'finesse',
  LIGHT: 'light',
  VERSATILE: 'versatile',
});

/**
 * [FIX APR 2026 sub-project G]: Tailwind text-color class per rarity.
 * Keyed by lowercase rarity id (RARITIES enum value). Use rarityColorClass(r)
 * to tolerate display-string inputs like "Very Rare".
 */
export const RARITY_COLORS = Object.freeze({
  [RARITIES.COMMON]:    'text-gray-300',
  [RARITIES.UNCOMMON]:  'text-green-400',
  [RARITIES.RARE]:      'text-blue-400',
  [RARITIES.VERY_RARE]: 'text-purple-400',
  [RARITIES.LEGENDARY]: 'text-orange-400',
  [RARITIES.ARTIFACT]:  'text-red-400',
});

/**
 * Normalize any rarity input (display-string or enum-value) to a Tailwind color class.
 * Replaces 4-deep ternary chains at ShopTab:337-340 and similar.
 */
export function rarityColorClass(rarity) {
  if (!rarity) return RARITY_COLORS[RARITIES.COMMON];
  const key = String(rarity).toLowerCase().replace(/\s+/g, '_');
  return RARITY_COLORS[key] || RARITY_COLORS[RARITIES.COMMON];
}

/**
 * [FIX APR 2026 sub-project G tail]: UI display modes.
 * Stored in settings.displayMode. Read across 7+ files (App.jsx, MobileNavigation,
 * DowntimesPanel, ShopTab, CharacterIdentityHeader, etc.). A typo in a single
 * consumer silently breaks only that file. Using the enum makes refactors safe.
 */
export const DISPLAY_MODES = Object.freeze({
  DEFAULT: 'default',
  SIMPLE: 'simple',
  JUST_INVENTORY: 'justInventory',
});

/**
 * [FIX APR 2026 sub-project G tail]: Crafting rulesets.
 * Stored in settings.craftingRuleset. Controls which formulas computeCraftingDuration
 * applies (src/data/craftingRules.js) and which UI fields DowntimesPanel shows.
 */
export const CRAFTING_RULESETS = Object.freeze({
  PHB: 'phb',
  XGTE: 'xgte',
});
